import React, { useState } from 'react'
import {
    BaseButtonOverrides,
    CancelModalButtonContainer,
    CancelModalButtonOverrides,
    CancelModalContent,
    ContentReasonText,
    DeleteSubscriptionContainer,
    ModalTitle,
    OnSubmitModalOverride,
    SelectArrowIcon,
    SelectOverrides
} from './DeleteSubscription.style'
import Button from 'parkdepot-shared/components/Buttons/Button'
import { BUTTON_KIND, BUTTON_SIZE } from 'parkdepot-shared/components/Buttons/types'
import { useTranslation } from 'react-i18next'
import Drawer from '@components/Drawer/Drawer.component'
import { CANCEL_USER_SUBSCRIPTION } from '@graphql/mutation/cancelUserSubscription.mutation'
import { useAuthenticatedMutation } from '@hooks/useAuth'
import useSubscriptions from '@hooks/useSubscriptions.hook'
import { homeRoute } from '@views/Home/route.const'
import { useHistory } from 'react-router-dom'
import { DateTime } from 'luxon'
import { Select, Value } from 'baseui/select'

interface Props {
    subId: string
    subscriptionUsed: boolean
}

const DeleteSubscription: React.FC<Props> = ({ subId, subscriptionUsed }) => {
    const { t } = useTranslation()
    const history = useHistory()
    const [openCancelSubscriptionModal, setOpenCancelSubscriptionModal] = useState(false)
    const [cancelReason, setCancelReason] = useState<Value>()
    const cancelSelectOptions: string[] =
        t('subscriptions.bookingDetails.cancelModalSelectOptions', {
            returnObjects: true
        }) ?? []

    const [loading, setLoading] = React.useState(false)
    const { removeSubscription } = useSubscriptions()

    const [cancelSubscription] = useAuthenticatedMutation({
        mutation: CANCEL_USER_SUBSCRIPTION,
        onCompleted: () => {
            removeSubscription(subId)
            setOpenCancelSubscriptionModal(false)
            history.push(homeRoute())
        },
        onError: () => {
            console.log('error')
        }
    })

    const onSubscriptionDelete = () => {
        setOpenCancelSubscriptionModal(true)
    }

    const onCancelClick = () => {
        setOpenCancelSubscriptionModal(false)
    }

    const onSubmitClick = async () => {
        setLoading(true)
        try {
            const reason = cancelReason?.[0]?.label ?? ''
            await cancelSubscription({ variables: { subscription_id: subId, reason } })
            history.push(homeRoute())
        } catch (e) {
            console.log(e)
        } finally {
            setLoading(false)
        }
    }

    return (
        <>
            <DeleteSubscriptionContainer>
                <Button
                    id="cancel-subscription-btn"
                    onClick={onSubscriptionDelete}
                    $kind={BUTTON_KIND.secondary}
                    $size={BUTTON_SIZE.medium}
                    overrides={{
                        BaseButton: BaseButtonOverrides
                    }}
                >
                    {t('subscriptions.bookingDetails.cancelSubscription')}
                </Button>
            </DeleteSubscriptionContainer>
            <Drawer
                isOpen={openCancelSubscriptionModal}
                content={
                    <CancelModalContent>
                        <ModalTitle>{t('subscriptions.bookingDetails.cancelModalTitle')}</ModalTitle>
                        {subscriptionUsed && (
                            <div>
                                {/* end_date is last day of the current month from today */}
                                {t('subscriptions.bookingDetails.cancelModalSubtitle', {
                                    end_date: DateTime.now().endOf('month').toFormat('dd.MM.yyyy')
                                })}
                            </div>
                        )}
                        <ContentReasonText>{t('subscriptions.bookingDetails.cancelModalSubtitle2')}</ContentReasonText>
                        <div>
                            <Select
                                options={cancelSelectOptions.map((option) => ({ id: option, label: option }))}
                                value={cancelReason}
                                onChange={(newValue) => {
                                    setCancelReason(newValue.value)
                                }}
                                placeholder={t('subscriptions.bookingDetails.cancelModalSelectOption')}
                                overrides={{
                                    ...SelectOverrides,
                                    SelectArrow: {
                                        component: () => <SelectArrowIcon className="wemolo-chevron-down" />
                                    }
                                }}
                            />
                        </div>
                        <CancelModalButtonContainer>
                            <Button
                                id="submit-cancel-subscription-btn"
                                isLoading={loading}
                                onClick={onSubmitClick}
                                $kind={BUTTON_KIND.primary}
                                overrides={OnSubmitModalOverride}
                            >
                                {t('subscriptions.bookingDetails.cancelModalConfirm')}
                            </Button>
                            <Button
                                id="undo-cancel-subscription-btn"
                                $kind={BUTTON_KIND.ghost}
                                onClick={onCancelClick}
                                overrides={CancelModalButtonOverrides}
                            >
                                {t('subscriptions.bookingDetails.cancelModalCancel')}
                            </Button>
                        </CancelModalButtonContainer>
                    </CancelModalContent>
                }
                onClose={onCancelClick}
            />
        </>
    )
}

export default DeleteSubscription
